import { Button, Divider, FormControl, Link, Modal, Stack, Typography } from '@mui/material';
import { cloneDeep } from 'lodash';
import  React, { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import ConfirmationDialog from '../../../components/Modal/ConfirmationDialog';
import { SaveExitModal } from '../../../components/Modal/SaveExitModal/SaveExitModal';
import ProgressModal from '../../../components/Progress/ProgressModal/ProgressModal';
import { ISpreadsheetGrid } from '../../../components/Spreadsheet/ISpreadsheetGrid';
import { Spreadsheet } from "../../../components/Spreadsheet/Spreadsheet";
import { AppContext } from '../../../core/context/appContextProvider';
import RoutingPaths from '../../../core/routing/routingPaths';
import { getClientTransactionMapping } from '../../../services/client.service';
import {
  getBaseUrl,
  getGlEntries,
  getInvestorCommitmentList,
  getPITDSummaryValues,
  getTransactions,
  getWorkbookById,
  getWorkbookLock,
  getWorkbooksByFund,
  lockWorkbook,
  saveWorkbook,
  unlockWorkbook
} from '../../../services/workbook.service';
import { DateTimeFormat } from '../../../utils/helpers/format.helper';
import { getQuarterStartEndDates } from '../../../utils/helpers/quarter.helper';
import { ClientTransTypeRow } from '../../../utils/types/clientTransMapping.type';
import { InvestorCommitment, TransactionTypeQuarterlySum, Workbook, WorkbookLockStatus } from '../../../utils/types/workbook.type';
import { applyMetricSigns } from './allocationshared/shared';
import { AllocationSpreadsheetManager } from './AllocationWorkbook.manager';
import { StyledBox } from './AllocationWorkbook.styles';
import { AllocationWorkbookInfo } from './allocationWorkbookInfo/AllocationWorkbookInfo';
import { InvestorIdReconciler } from './investorIdReconciler/InvestorIdReconciler';
import { NewTransactionTypeSelector } from './newTransactionTypeSelector/NewTransactionTypeSelector';
import { SaveAsPrompt } from './saveAsPromptDialog/SaveAsPrompt';
import { SubmitToPcap } from './submitToPcap/SubmitToPcap';

// Hack alert: I tried to use UseState but doesn't work in events fired by treegrid
//  Each of these need to be set in the usefeect hook and handleNavigateAway()
let workbookId: string|undefined = undefined;
let ssManager: AllocationSpreadsheetManager;
let savedForPcap = false;
let savedForRolloverNewWB = false;
let rolloverNewWB: Workbook|undefined;
let savedForSaveAs: string|undefined = undefined;
let showSaveAsSavePromt = false;
let saveAsWB: Workbook|undefined;
let currentSheetIsAllocations = true;
//let workbookReadonly = true;
let workbookLockStatus: WorkbookLockStatus;
let hasUnsavedChanges = false;
let investorReconciler: { workbook: Workbook, investorCommitments: InvestorCommitment[], grid: ISpreadsheetGrid }|undefined = undefined;

export const AllocationWorkbook: React.FC = () => { 
  const defaultTemplateUrl = '/Layouts/allocations_template.xlsx';

  const { state } = useContext(AppContext);
  const username = state.loginUser.currentUser?.username || "";
  const isSuperAdmin = state.loginUser.currentUser?.scopeRole === "SUPER_ADMIN";
  const history = useHistory();
  const { fundId, workbookId: workbookIdParam } = useParams<{ fundId: string, workbookId: string }>();

  const [treeGridLibraryLoaded, setTreeGridLibraryLoaded] = useState(false);
  const [ssManagerState, setSsManagerState] = useState<AllocationSpreadsheetManager>();
  const [spreadsheetUrl, setSpreadsheetUrl] = useState('');
  const [isLoading, setIsLoading] = useState<string|undefined>();
  const [showAllocationWBddialog, setShowAllocationWBddialog] = useState(false);
  const [showNewTransactionDialog, setShowNewTransactionDialog] = useState(false);
  const [showSubmitToPcapDialog, setShowSubmitToPcapDialog] = useState(false);
  const [showPcapSavePromt, setShowPcapSavePromt] = useState(false);
  const [showModelSaveAs, setShowModelSaveAs] = useState(false);
  //const [showReadonlyMessageDialog, setShowReadonlyMessageDialog] = useState(false);
  const [showGoToGl, setShowGoToGl] = useState(false);
  const [showGlConnectError, setShowGlConnectError] = useState(false);
  const [errorTitle, setErrorTitle] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showColumnTypeModal, setShowColumnTypeModal] = useState(false);
  const [handleColsAddCols, setHandleColsAddCols] = useState<string[]>([]);
  const [handleColsAddToCol, setHandleColsAddToCol] = useState('');
  const [showRolloverNewWBDialog, setShowRolloverNewWBDialog] = useState(false);
  const [invalidPasteText, setInvalidPasteText] = useState<string|undefined>(undefined);
  const [forceReloader, setForceReloader] = useState(0);
  
  const location = useLocation<Workbook>();

  useEffect(() => {

    const elementId = 'TreeGridComponet';
    const existingScript = document.getElementById(elementId);

    if (existingScript) {
      setTreeGridLibraryLoaded(true);
    } else {
      const script = document.createElement('script');

      script.src = '/Grid/GridED.js';
      script.id = elementId;
      document.body.appendChild(script);
      script.onload = () => { 
        setTreeGridLibraryLoaded(true);
      };
    }


    if(location.state) {
      workbookId = undefined;
      setSpreadsheetUrl(defaultTemplateUrl);
      hasUnsavedChanges = true;
    } else {
      workbookId = workbookIdParam;
      setSpreadsheetUrl(`${getBaseUrl()}workbooks/${workbookIdParam}/file`);
      hasUnsavedChanges = false;
    }

    savedForPcap = false;
    savedForRolloverNewWB = false;
    savedForSaveAs = undefined;
    showSaveAsSavePromt = false;
    rolloverNewWB = undefined;
    saveAsWB = undefined;
    currentSheetIsAllocations = true;
    //workbookReadonly = true;
    investorReconciler = undefined;
  }, []);


  async function handleRenderFinish(grid: ISpreadsheetGrid, sheetName: string|undefined) {
    if(sheetName && sheetName !== 'Allocations') {
      currentSheetIsAllocations = false;
      return;
    } 

    currentSheetIsAllocations = true;

    if(ssManager && !rolloverNewWB && !saveAsWB) {
      return;
    }

    setIsLoading('Loading');

    let wb: Workbook; 
    let isNew: boolean;
    
    if(rolloverNewWB) {
      isNew = false;
      wb = rolloverNewWB;
      rolloverNewWB = undefined;
    } else if(saveAsWB) {
      isNew = false;
      wb = saveAsWB;
    } else if(location.state) {
      isNew = true;
      wb = location.state;
    } else {
      isNew = false;
      wb = await getWorkbookById(workbookId!);

      const clientTransMappingsResponse = await getClientTransactionMapping(wb.tenantId);
      const lastQuarter = Math.max(...wb.transactionColumns.map(x => x.quarterNumber));

      applyMetricSigns(wb.transactionColumns.filter(c => c.quarterNumber===lastQuarter), clientTransMappingsResponse);
    }

    // eslint-disable-next-line no-console
    console.log(`--------- handleRenderFinish() - isNew ${isNew} -----------`);


    const investorCommitments = await getInvestorCommitmentList(wb.fund.id, wb.startQuarterNumber, wb.startQuarterYear, wb.commitmentTransactionTypeIds);

    if(wb.version === 1) {
      investorReconciler = { workbook: wb, investorCommitments, grid };
      setIsLoading(undefined); 
    } else {
      renderSpreadsheetFromGridInfo(grid, wb, isNew, investorCommitments);
    }
  }

  async function renderSpreadsheetFromGridInfo(grid: ISpreadsheetGrid, wb: Workbook, isNew: boolean, investorCommitments: InvestorCommitment[]) {

    setIsLoading('Allocations workbook is being calculated and reconciled, this may take a minute.');

    const quarterStartEnd = getQuarterStartEndDates(wb.startQuarterNumber, wb.startQuarterYear);
    const thruDateAdjusted = new Date(quarterStartEnd.startDate.getTime() - 86400000);
    const pitdSummaryValues = await getPITDSummaryValues(wb.fund.id, thruDateAdjusted);
    
    const investorIDs = investorCommitments.map(i => i.investorId);
    const transTypes = wb.transactionColumns.filter(t => t.useArkTransactionValues).map(t => t.transTypeId);


    const startQuarter = wb.startQuarterNumber;
    const lastQuarter = wb.transactionColumns.reduce((prevVal,currVal) => currVal.quarterNumber > prevVal ? currVal.quarterNumber : prevVal, 0);
    const allTransTypeSums = new Map<number, TransactionTypeQuarterlySum[]>();

    for(let q = startQuarter; q <= lastQuarter; q++) {
      // eslint-disable-next-line
      const transTypeSums = await getTransactions(wb.fund.id, q, wb.startQuarterYear, investorIDs, transTypes);

      allTransTypeSums.set(q, transTypeSums);
    }
    
    ssManager = new AllocationSpreadsheetManager(isNew, wb, grid, investorCommitments, pitdSummaryValues, allTransTypeSums,
       () => {

        // eslint-disable-next-line no-console
        console.log(`---------  new AllocationSpreadsheetManager callback -   isNew ${isNew} -----------`);

        if(!isNew) {
          ensureWorkbookLockState(workbookId!);
        } else {
          //workbookReadonly = false;
        }

        setIsLoading(undefined); 
      });

    setSsManagerState(ssManager);
  }

  function onToolbarButtonClick(buttonName: string) {
    if(buttonName === "RollForward") {
      onRollover();
    } else if(buttonName === "GetFromGl") {
      onGetFromGl();
    } else if(buttonName === "SubmitToPcap") {
      onSubmitToPcap();
    } else if (buttonName === "AllocationsConfig") {
      onLoadAllocationsConfig();
    } else if (buttonName === "CustSave") {
      onSaveSpreadsheet();
    } else if (buttonName === "CustSaveAs") {
      onSaveAsSpreadsheet();
    }
  }

  function onGetFromGl() {
    // if(workbookReadonly) {
    //   alertUserWBisReadonly();
    //   return;
    // }

    setIsLoading('Loading GL values');

    const fundId = ssManager.getFundId();
    const frequency = ssManager.getWorkbookFrequency();
    const year = ssManager.getYear();

    getGlEntries(fundId, frequency, year).then(response => {

      if(typeof response !== 'string') {
        ssManager.renderGlEntries(response);
        hasUnsavedChanges = true;
      } else {
        if(response === 'Request failed with status code 403') {
          setShowGoToGl(true);
        } else {
          setShowGlConnectError(true);
        }
      }

      setIsLoading(undefined);
    });
  }


  //  Start Workbook Locking functions

   //async function ensureWorkbookLockState(wbId: string) {
  function ensureWorkbookLockState(wbId: string) {
    
      // eslint-disable-next-line no-console
      console.log(`--------- ensureWorkbookLockState() - wbId ${wbId} -----------`);


  //   const lockStatusResponse = await getWorkbookLock(wbId);

  //   if(!lockStatusResponse.locked && lockStatusResponse.lockedBy !== username) {
  //     lockWorkbook(wbId);
  //   }

  //   workbookReadonly = lockStatusResponse.locked && lockStatusResponse.lockedBy !== username;

  //   if(workbookReadonly) {
  //     workbookLockStatus = lockStatusResponse;
  //     alertUserWBisReadonly();
  //   }
   }

  //async function handleNavigateAway(force: boolean = false) {
  function handleNavigateAway(force: boolean = false) {
    // if((force || !workbookReadonly) && workbookId) {
    //   await unlockWorkbook(workbookId!);
    // }

    //@ts-ignore
    ssManager = undefined;
    workbookId = undefined;
  }

  // function alertUserWBisReadonly() {
  //   setShowReadonlyMessageDialog(true);
  // }

  // async function forceSuperAdminLock() {
  //   if(isSuperAdmin) {
  //     await handleNavigateAway(true);
  //     await lockWorkbook(workbookIdParam!);

  //     workbookReadonly = false;
  //     setShowReadonlyMessageDialog(false); 
  //   }
  // }

  // End Workbook Locking functions




// Begin saving functions

  function onSaveSpreadsheet() {
    
    // if(workbookId) {
    //   ensureWorkbookLockState(workbookId);// Check and reset lock in case superadmin locked WB while open
    // }

    // if(workbookReadonly) {
    //   alertUserWBisReadonly();
    //   setIsLoading(undefined);
    //   return;
    // }

    setIsLoading('Saving workbook');

    setTimeout(() => {
      ssManager.saveSpreadsheet();
    }, 1);
  }

  function onSaveAsSpreadsheet() {
    // if(workbookReadonly) {
    //   alertUserWBisReadonly();
    //   return;
    // }

    if(hasUnsavedChanges) {
      showSaveAsSavePromt = true;
      setForceReloader(forceReloader+1);
    } else {
      setShowModelSaveAs(true);
    }
  }

  async function handleSave(fileBlob: Blob) {
    // if(workbookReadonly) {
    //   alertUserWBisReadonly();
    //   setIsLoading(undefined);
    //   return;
    // }

    if(savedForSaveAs) {
      saveForSaveAs(fileBlob);
      return;
    }

    const workbook = ssManager.getWorkbookInfo();

    const savedWb = await saveWorkbook(workbook, fileBlob);
    const savedWbId = savedWb.id;

    workbookId = savedWbId;
    lockWorkbook(savedWbId!);


    ssManager.setUpdatedValuesfromSave(savedWb);

    if(savedForPcap) {
      showPcapDialog();
    } else if(savedForRolloverNewWB) {
      saveRolloverForNewWB(workbook);
    }

    hasUnsavedChanges = false;

    setIsLoading(undefined);

    if(showSaveAsSavePromt) {
      showSaveAsSavePromt = false;
      onSaveAsSpreadsheet();
    }
  }

  async function saveForSaveAs(fileBlob: Blob) {
    if(workbookId) {
      await unlockWorkbook(workbookId);
    }

    const workbook = ssManager.getWorkbookInfo();

    setWorkbookAsNew(workbook);
    
    const savedWb = await saveWorkbook(workbook, fileBlob);

    workbookId = savedWb.id;
    savedForSaveAs = undefined;
    saveAsWB = savedWb;
    setIsLoading(undefined);

    // clearing spreadsheetUrl and resetting it in setTimeout(), forces treegrid component to reload
    setSpreadsheetUrl('');
    
    setTimeout(() => {
      setSpreadsheetUrl(`${getBaseUrl()}workbooks/${savedWb.id}/file`);
    }, 100);
  }

  function handleSaveAsClose(saveClicked: boolean, newFileName?: string) {
    setShowModelSaveAs(false);
    
    if(!saveClicked) {
      savedForSaveAs = undefined;
      return;
    } 

    savedForSaveAs = newFileName;
    ssManager.setReportName(newFileName!);
    setIsLoading(`Saving as ${newFileName}`);

    setTimeout(() => {
      ssManager.saveSpreadsheet();
    }, 1);
  }

// End saving functions


  //Start rollover functions

  function onRollover() {
    // if(workbookReadonly) {
    //   alertUserWBisReadonly();
    //   return;
    // }

    if(ssManager.isWorkbookReadyforNewWB()) {
      setShowRolloverNewWBDialog(true);
      return;
    }

    setIsLoading('Allocations workbook is being rolled forward and calculated, this may take a minute.');
    setTimeout(async () => {
      const wb = ssManager.getWorkbookInfo();
      const startQuarter = ssManager.getFirstQuarter();
      const newQuarterNumber = ssManager.getLastQuarter() + 1;

      const investorCommitments = await getInvestorCommitmentList(wb.fund.id, newQuarterNumber, wb.startQuarterYear, wb.commitmentTransactionTypeIds);
      const investorIDs = investorCommitments.map(i => i.investorId);
      const transTypes = wb.transactionColumns.filter(t => t.useArkTransactionValues).map(t => t.transTypeId);

      const allTransTypeSums = new Map<number, TransactionTypeQuarterlySum[]>();
  
      for(let q = startQuarter; q <= newQuarterNumber; q++) {
        // eslint-disable-next-line
        const transTypeSums = await getTransactions(wb.fund.id, q, wb.startQuarterYear, investorIDs, transTypes);
  
        allTransTypeSums.set(q, transTypeSums);
      }

      ssManager.rolloverTransactionColumns(investorCommitments, allTransTypeSums, () => { setIsLoading(undefined); });

      hasUnsavedChanges = true;
    }, 1);
  }

  function handleRolloverNewWB() {
    savedForRolloverNewWB = true;
    setIsLoading('Loading');
    setShowRolloverNewWBDialog(false);

    setTimeout(() => {
      ssManager.saveSpreadsheet();
    }, 1);
  }

  function saveRolloverForNewWB(sourceWorkbook: Workbook) {
    const workbook = cloneDeep(sourceWorkbook);

    setWorkbookAsNew(workbook, workbook.startQuarterYear+1, 1);

    workbook.reportName = `${workbook.reportName} - Copy`;

    savedForRolloverNewWB = false;
    setIsLoading('Creating new workbook');

    // clearing spreadsheetUrl and resetting it in setTimeout(), forces treegrid component to reload
    setSpreadsheetUrl('');

    setTimeout(() => {
      setSpreadsheetUrl(defaultTemplateUrl);
      rolloverNewWB = workbook;
    }, 100);
  }

  //End rollover functions



  // start config functions
  function onLoadAllocationsConfig() {
    // if(workbookReadonly) {
    //   alertUserWBisReadonly();
    //   return;
    // }

    setShowAllocationWBddialog(true);
  }

  function handleSaveFromWorkbookInfoDialog(workbook: Workbook) {
    setShowAllocationWBddialog(false);

    setIsLoading('loading');
    setTimeout(() => {
      ssManager.applyWorkbookConfigChanges(workbook);
      setIsLoading(undefined);
    }, 1);
  }
  // End config functions



  // Start PCAP Function
  function onSubmitToPcap() {
    // if(workbookReadonly) {
    //   alertUserWBisReadonly();
    //   return;
    // }

    if(hasUnsavedChanges) {
      setShowPcapSavePromt(true);
    } else {
      showPcapDialog();
    }
  }

  function showPcapDialog() {
    setShowSubmitToPcapDialog(true);
    savedForPcap = false;
    setIsLoading(undefined);
  }

  function handleSubmitToPcap(updatedWorkbook: Workbook, needsSaving: boolean) {
    setShowSubmitToPcapDialog(false);
    if(needsSaving) {
      ssManager.updateTransactionColumnsLockStatus(updatedWorkbook);
    }
  }

  function handleSaveWBAndDisplaySubmitToPcap() {
    savedForPcap = true;
    setIsLoading('Saving');
    setShowPcapSavePromt(false);

    setTimeout(() => {
      ssManager.saveSpreadsheet();
    }, 1);
  }

  // End PCAP Function


  // Start column adding functions

  function handleColsAdd(cols: string[], toCol: string, _right: boolean, _empty: boolean) {
    // if(workbookReadonly) {
    //   alertUserWBisReadonly();
    //   return true;
    // }

    if(!currentSheetIsAllocations) return false;

    if(ssManager.canAddAllocationColumn(toCol)) {
      setIsLoading('Adding column');
      setTimeout(() => {
        ssManager.AutoAddAllocationColumns(cols.length, toCol);
        hasUnsavedChanges = true;
        setIsLoading(undefined);
      }, 1);

      return true;
    }

    if(ssManager.canAddTransactionCol(toCol)) {
      setHandleColsAddCols(cols);
      setHandleColsAddToCol(toCol);

      setShowColumnTypeModal(true);
      return true;
    }

    setErrorTitle('Invalid New Column Position');
    setErrorMessage('You can not add a column here');
    return true;
  }

  function handleOnShowColumnTypeModal() {
    setShowNewTransactionDialog(true);
    setShowColumnTypeModal(false);
  }

  function handleOnCreateUserTypeModal() {
    // if(workbookReadonly) {
    //   alertUserWBisReadonly();
    //   return;
    // }

    setShowColumnTypeModal(false);

    setIsLoading('Adding column');
    setTimeout(() => {
      ssManager.autoAddTransactionsColumns(handleColsAddCols.length, handleColsAddToCol);
      hasUnsavedChanges = true;
      setIsLoading(undefined);
    }, 1);
  }

  function handleNewTransactionTypeSelectorSave(clientTransTypeRows: ClientTransTypeRow[]) {
    // if(workbookReadonly) {
    //   alertUserWBisReadonly();
    //   return;
    // }

    setShowNewTransactionDialog(false);

    setIsLoading('Adding column');

    setTimeout(() => {
      ssManager.addTransactionsColumnsFromClientTransTypeRows(clientTransTypeRows, handleColsAddToCol);

      hasUnsavedChanges = true;

      setHandleColsAddCols([]);
      setHandleColsAddToCol('');

      setIsLoading(undefined);
    }, 1);

  }

    // End column adding functions


  function handleRowAdd(parentRow: number) {
    if(!currentSheetIsAllocations) return false;

    const firstEditableRow = ssManager.getFirstEditableRow();

    return parentRow >= firstEditableRow;
  }



// start cell permission management 
  function handleStartEdit(row: number, col: string): boolean {
    // if(workbookReadonly) {
    //   alertUserWBisReadonly();
    //   return true;
    // }

    if(row === undefined && col === "Sheet1") return true;

    if(!currentSheetIsAllocations) return false;

    return !ssManager.isCellEditable(row, col);
  }
  
  function handleEndEdit(row: number, col: string, save: boolean, value: string): boolean {
    // if(workbookReadonly) {
    //   alertUserWBisReadonly();
    //   return false;
    // }

    if(!currentSheetIsAllocations) return false;

    ssManager.editCell(row, col, value);

    hasUnsavedChanges = true;
    
    return false;
  }

  function handleOnCanPaste(startCol: string, startRow: number, numOfCols: number, numOfRows: number): boolean {
    // if(workbookReadonly) {
    //   alertUserWBisReadonly();
    //   return false;
    // }

    if(!currentSheetIsAllocations) return false;


    const invalidCells = ssManager.isCellEditableRange(startCol, startRow, numOfCols, numOfRows);
    
    if(invalidCells.length>0) {
      const invalidCellsStr = invalidCells.join(',');

      setInvalidPasteText(invalidCellsStr);
    } 

    return invalidCells.length > 0;
  }

  function handleOnCanColDelete(col: string) {
    if(!currentSheetIsAllocations) return true;

    const firstEditableCol = ssManager.getFirstEditableFreeFormCol();
    const comp = ssManager.compareCols(col, firstEditableCol);
    const canDelete = comp >= 0;

    return canDelete;
  }

  function handleOnCanRowDelete(row: number) {
    if(!currentSheetIsAllocations) return true;

    const canDelete = row > ssManager.getFirstEditableRow();

    return canDelete;
  }

// End cell permission management 

  function setWorkbookAsNew(wb: Workbook, startYear?: number, startQuarter?: number) {
    wb.id = undefined;
    wb.startQuarterYear = startYear ?? wb.startQuarterYear;
    wb.startQuarterNumber = startQuarter ?? wb.startQuarterNumber;

    const firstQuarterInTransCols = wb.transactionColumns
        .reduce((prevVal,currVal) => currVal.quarterNumber < prevVal ? currVal.quarterNumber : prevVal, 5);

    wb.transactionColumns = wb.transactionColumns.filter(c => c.quarterNumber === firstQuarterInTransCols)
        .map(c => {
            c.id = undefined;
            c.locked = false;
            c.quarterNumber = wb.startQuarterNumber;
            c.transSubmitError = false;
            
          return c;
        });
    
    wb.allocationColumns.forEach(ac => {
      ac.id = undefined;
    });

    wb.investorRows.forEach(r => {
      r.id = undefined;
    });
  }


  function handleOnErrorModalOk() {
    setErrorTitle('');
    setErrorMessage('');
  }

  async function handleRetreiveValidNames() {
    const workbooksResponse = await getWorkbooksByFund(ssManager.getFundId());
    const nameList = workbooksResponse.map(wb => wb.reportName);

    return nameList;
  }

  function investorIdReconcilerOnSave() {
    if(investorReconciler) {
      renderSpreadsheetFromGridInfo(investorReconciler.grid, investorReconciler.workbook, false, investorReconciler.investorCommitments);
    }
  }

  function investorReconcilerOnCancel() {
    history.push(`${RoutingPaths.FundAllocationsList}/${fundId}`);
  }

  return (
    <>
      {
        spreadsheetUrl && treeGridLibraryLoaded &&
          <Spreadsheet
            spreadsheetUrl={spreadsheetUrl}
            exportFilename="Allocation.xlsx"
            hasUnsavedChanges={hasUnsavedChanges}
            onToolbarButtonClick={onToolbarButtonClick}
            onRenderFinish={handleRenderFinish}
            onSave={handleSave}
            onColsAdd={handleColsAdd}
            onRowAdd={handleRowAdd}
            onStartEdit={handleStartEdit}
            onEndEdit={handleEndEdit}
            onCanColDelete={handleOnCanColDelete}
            onCanRowDelete={handleOnCanRowDelete}
            onCanPaste={handleOnCanPaste}
          /> 
      }

      {
        showAllocationWBddialog &&
          <AllocationWorkbookInfo
            workbook={ssManager.getWorkbookInfo()}
            onOk={handleSaveFromWorkbookInfoDialog}
            onCancel={() => {setShowAllocationWBddialog(false);}}
          />
      }

      <ProgressModal
        id="workbook_loading"
        showProgress={isLoading !== undefined}
        text={isLoading}
      />

      <SaveExitModal
        isBlocked={hasUnsavedChanges}
        title="Leave Page?"
        content="Your changes have not been saved. Are you sure you want to leave this workbook?"
        actionTitle="Save Workbook"
        confirmNavigationTitle="Exit Without Saving"
        onActionClick={onSaveSpreadsheet}
        onNavigateAway={handleNavigateAway}
      />

      {/* <ConfirmationDialog
        open={showReadonlyMessageDialog}
        onClose={() => { setShowReadonlyMessageDialog(false); }}
        id="modal_readonly_message"
        actions={[
          {
            label: 'Open read only',
            onClick: () => { setShowReadonlyMessageDialog(false); },
            id: 'ok_button',
            variant: 'contained',
            color: 'primary'
          },
          {
            label: 'Force unlock',
            onClick: forceSuperAdminLock,
            id: 'ok_button',
            variant: 'contained',
            color: 'primary',
            hide: !isSuperAdmin
          }

        ]}
        content={ 
          <> 
            This workbook is locked by { workbookLockStatus?.lockedBy } <br/>
            on { workbookLockStatus?.lockedAt ? DateTimeFormat.longDateTime(workbookLockStatus?.lockedAt) : null } 

            {
              isSuperAdmin && (
                <>
                  <br/><br/>
                  As super admin, you can override this and lock this workbook under your username.  Once you exit this workbook, the lock will be cleared as usual.
                </>
              )
            }
          </> 
        }
        title="Workbook is read only"
      /> */}

      <ConfirmationDialog
        id="rollover_new_wb_dialog"
        open={showRolloverNewWBDialog}
        onClose={() => { setShowRolloverNewWBDialog(false); }}
        actions={[
          {
            id: "ok_button",
            label: "Continue",
            variant: 'contained',
            color: 'primary',
            onClick: handleRolloverNewWB
          }, {
            id: "cancel_cancel",
            label: "Cancel",
            variant: 'contained',
            color: 'secondary',
            onClick: () => { setShowRolloverNewWBDialog(false); }
          }
        ]}
        title="Rollover New Workbook"
        content="This will save this workbook and create a new workbook.  Do you want to continue?"
      />

      <Modal
        id="modal_transacrtion_type_column"
        aria-labelledby="modal_newtranstype_title"
        aria-describedby="modal_newtranstype_description"
        open={showColumnTypeModal}
        onClose={() => { setShowColumnTypeModal(false); }}
      >
        <StyledBox>

          <Typography id="modal_newtranstype_title" component="h2">
                        Ark Transaction Type or Free Form Column
          </Typography>
          <Typography id="modal_newtranstype_description" variant='body2'>
                        Ark Transaction Type or Free Form Column
          </Typography>
          <Divider />
          <FormControl>
            <Stack direction="row" spacing={4} sx={{
              mt: 3 
            }}>
              <Button
                id="btn_save_new_trans_type"
                variant="contained"
                onClick={handleOnShowColumnTypeModal}
              >
                                Create Transaction Type Column
              </Button>
              <Button
                id="btn_save_new_user_type"
                variant="contained"
                onClick={handleOnCreateUserTypeModal}
              >
                                Create User Column
              </Button>

              <Button id="btn_cancel_save" variant="outlined" onClick={() => { setShowColumnTypeModal(false); }}>Cancel</Button>
            </Stack>
          </FormControl>

        </StyledBox>
      </Modal>

      {
        showNewTransactionDialog ?
          <NewTransactionTypeSelector
            workbook={ssManager.getWorkbookInfo()}
            onSave={handleNewTransactionTypeSelectorSave}
            onCancel={() => setShowNewTransactionDialog(false)}
          /> : null
      }

      <ConfirmationDialog
        open={showGoToGl}
        onClose={() => {setShowGoToGl(false); }}
        id="modal_go_to_gl"
        actions={[
          {
            label: 'Cancel',
            onClick: () => {setShowGoToGl(false); },
            id: 'cancel_button',
            variant: 'outlined',
            color: 'error'
          }
        ]}
        content={
          <div>
            No General Ledger connected. Click&nbsp; 
            <Link 
              component="button" 
              variant="body1" 
              onClick={() => { history.push(`${RoutingPaths.TransactionMapper}/${ssManagerState!.getFundId()}`); }}
            >here</Link> 
            &nbsp;to authenticate with a GL.
          </div>}
        title="GL Connection"
      />


      <ConfirmationDialog
        open={showGlConnectError}
        onClose={() => {setShowGlConnectError(false); }}
        id="modal_prompt_save_first"
        actions={[
          {
            label: 'Try Again',
            onClick: () => {setShowGlConnectError(false); onGetFromGl();},
            id: 'try_again_button',
            variant: 'contained',
            color: 'primary'
          },
          {
            label: 'Cancel',
            onClick: () => {setShowGlConnectError(false); },
            id: 'cancel_button',
            variant: 'outlined',
            color: 'error'
          }
        ]}
        content="Something went wrong when trying to connect to GL service.  Please try again. if the problem persists, contact support for further help."
        title="Connection Error"
      />


      {
        showModelSaveAs &&
          <SaveAsPrompt
            onRetreiveValidNames={handleRetreiveValidNames}
            onClose={handleSaveAsClose}
          />
      }

      <ConfirmationDialog
        open={showPcapSavePromt}
        onClose={() => {setShowPcapSavePromt(false); }}
        id="modal_prompt_save_first"
        actions={[
          {
            label: 'Save',
            onClick: handleSaveWBAndDisplaySubmitToPcap,
            id: 'save_button',
            variant: 'contained',
            color: 'primary'
          },
          {
            label: 'Cancel',
            onClick: () => {setShowPcapSavePromt(false); },
            id: 'cancel_button',
            variant: 'outlined',
            color: 'error'
          }
        ]}
        content="This workbook must be saved before proceeding."
        title="Confirm Save"
      />

      <ConfirmationDialog
        open={showSaveAsSavePromt && !isLoading}
        onClose={() => {showSaveAsSavePromt = false; setForceReloader(forceReloader+1); }}
        id="modal_saveAs_prompt_save_first"
        actions={[
          {
            label: 'Save',
            onClick: onSaveSpreadsheet,
            id: 'save_button',
            variant: 'contained',
            color: 'primary'
          },
          {
            label: 'Cancel',
            onClick: () => {showSaveAsSavePromt = false;setForceReloader(forceReloader+1); },
            id: 'cancel_button',
            variant: 'outlined',
            color: 'error'
          }
        ]}
        content="This workbook must be saved before proceeding."
        title="Confirm Save"
      />



      {
        showSubmitToPcapDialog &&
          <SubmitToPcap
            workbook={ssManager.getWorkbookInfo()}
            allocationSpreadsheetManager={ssManager}
            quarterNumber={ssManager.getLastQuarter()}
            onClose={handleSubmitToPcap}
          />
      }

      {
        investorReconciler && 
          <InvestorIdReconciler
            workbook={investorReconciler.workbook} 
            investorCommitments={investorReconciler.investorCommitments}
            grid={investorReconciler.grid}
            onSave={ investorIdReconcilerOnSave }
            onCancel={ investorReconcilerOnCancel }
          />
      }

      <ConfirmationDialog
        open={errorMessage !== ''}
        onClose={() => {handleOnErrorModalOk();}}
        id="modal_error"
        actions={[
          {
            label: 'Ok',
            onClick: handleOnErrorModalOk,
            id: 'ok_button',
            variant: 'contained',
            color: 'primary'
          }
        ]}
        content={errorMessage}
        title={errorTitle}
      />

      <ConfirmationDialog
        open={!!invalidPasteText}
        onClose={() => setInvalidPasteText(undefined)}
        id="modal_invalid_paste"
        actions={[
          {
            label: 'Cancel',
            onClick: () => {setInvalidPasteText(undefined);},
            id: 'cencel_button',
            variant: 'contained',
            color: 'primary'
          }
        ]}
        title={"Invalid Paste"}
        content={(
          <>
            One or more columns cannot be pasted over because they are pulled from data in Ark. If you would like to change this data, please do so outside of the Allocations workbook.
            <br/><br/>
            {invalidPasteText}
          </>)}
      />
    </>
  );
};
